/**
 * Asking the refresh cookie who we are — in ONE place, at most once at a time.
 *
 * There used to be two of these. `api.ts` held a single-flight promise so that
 * a burst of parallel 401s could not rotate the same token twice, and its
 * comment said "all callers share one promise" — while `session.ts` ran its
 * own `fetch` to the same endpoint at boot, sharing nothing. Two independent
 * refreshes of one cookie is exactly the race the promise existed to prevent,
 * and the second one was invisible because it lived in a different file.
 *
 * The server forgives a replay within a few seconds now (`REPLAY_GRACE_MS`),
 * so this is no longer the only thing standing between a reload and a
 * sign-out. It is still worth having: a token rotated twice for no reason is
 * two rows, two cookie writes, and a window — however short — in which a
 * stolen token would not trip the reuse detector. Not racing is better than
 * being forgiven for it.
 *
 * This module owns the network call; `session.ts` stays a pure store, which is
 * also what keeps the imports acyclic (session ← refresh ← api, never back).
 */
import { setSession, clearSession, finishRestoring } from './session';

const BASE = '/api/v1';

let inFlight: Promise<void> | null = null;

/** Thrown when the cookie is absent, expired, or its family was revoked. */
export class RefreshFailed extends Error {
  constructor() { super('Your session has expired. Please sign in again.'); }
}

/**
 * Rotate the refresh cookie and adopt the session it returns.
 *
 * Callers share one promise: whoever asks while a refresh is in flight waits
 * for that one instead of starting a second.
 */
export function refreshOnce(): Promise<void> {
  inFlight ??= (async () => {
    // No body and no token: the HttpOnly cookie is the credential, and this
    // code could not read it even if it wanted to. That is the point — an
    // injected script has nothing here to steal.
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'same-origin',
    });
    if (!res.ok) {
      clearSession();
      throw new RefreshFailed();
    }
    setSession(await res.json());
  })().finally(() => { inFlight = null; });

  return inFlight;
}

/**
 * The boot-time restore: same call, but a failure is the ordinary state of a
 * first-time visitor rather than an error worth showing anybody.
 */
export async function restoreSession(): Promise<void> {
  try {
    await refreshOnce();
  } catch {
    // A 401 means no cookie or an expired one. A network error means offline
    // at boot. Both are "signed out", neither is a broken app.
    finishRestoring();
  }
}
